import React, { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  styled,
  tableCellClasses,
} from '@mui/material';
import { WebGlBenchmark, WebGlBenchmarkResult } from '@fabric-web/3d-viewer';
import { RunTest } from './RunTest';
import Button from '../shared/Button';
import { SystemTestStatusBgColor, SystemTestStatusColor } from '@/constants';

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: '#E7EEF6',
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: '1.4rem',
  },
}));

const StyledTableRow = styled(TableRow)(() => ({
  'td, th, tr': {
    border: 0,
  },
}));

export const Hardware = () => {
  const [result, setResult] = useState<WebGlBenchmarkResult | undefined>();
  const [testRunning, setTestRunning] = useState<boolean>(false);
  const [progressPercent, setProgressPercent] = useState<number>(0);

  const onProgress = useCallback(
    (percent: number, intermediateResult: WebGlBenchmarkResult) => {
      setProgressPercent(percent);
      setResult(intermediateResult);
      if (intermediateResult.isCompleted) {
        setTestRunning(false);
      }
    },
    [setProgressPercent, setResult]
  );

  const renderQualityStatus = useMemo(() => {
    if (!result) return 'N/A';

    if (result.recomendedPointBudget >= 10_000_000) {
      return 'Good';
    }
    if (result.recomendedPointBudget > 3_000_000) {
      return 'Weak';
    }

    return 'Poor';
  }, [result]);

  const rerun = useCallback(() => {
    setTestRunning(true);
    setResult(undefined);
    setProgressPercent(0);
  }, []);

  return (
    <>
      {!result?.isCompleted && (
        <RunTest
          text={testRunning ? 'Please Wait...' : 'There is no hardware test result to display'}
          loading={testRunning}
          progress={progressPercent.toFixed(0)}
          onRunTest={() => {
            setTestRunning(true);
          }}
        />
      )}
      {testRunning && (
        <div style={{ opacity: 0 }}>
          <WebGlBenchmark onComplete={(result0) => setResult(result0)} onProgress={onProgress} />
        </div>
      )}

      {result?.isCompleted && (
        <Box
          data-testid="hardware-container"
          sx={{ padding: '90px 137px', mt: '60px', width: '100%', backgroundColor: '#6E81980D' }}
        >
          <TableContainer>
            <Table sx={{ minWidth: 700 }} aria-label="customized table">
              <TableHead>
                <TableRow>
                  <StyledTableCell>Hardware</StyledTableCell>
                  <StyledTableCell align="left">Your system</StyledTableCell>
                  <StyledTableCell align="left">Ideal system</StyledTableCell>
                  <StyledTableCell align="right">Status</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <StyledTableRow>
                  <StyledTableCell align="left">GPU</StyledTableCell>
                  <StyledTableCell align="left">
                    <div style={{ textOverflow: 'elipsis', textAlign: 'left' }}>
                      {result?.gpuInfo.renderer}
                    </div>
                  </StyledTableCell>
                  <StyledTableCell align="left">Dedicated GPU</StyledTableCell>
                  <StyledTableCell align="right">
                    <Chip
                      sx={{
                        backgroundColor: SystemTestStatusBgColor[renderQualityStatus],
                        color: SystemTestStatusColor[renderQualityStatus],
                      }}
                      label={renderQualityStatus}
                    />
                  </StyledTableCell>
                </StyledTableRow>

                <StyledTableRow>
                  <StyledTableCell align="left">Rendered quality</StyledTableCell>
                  <StyledTableCell align="left">
                    <div style={{ textOverflow: 'elipsis', textAlign: 'left' }}>
                      {(result?.recomendedPointBudget ?? 0) / 1_000_000} Million Points
                    </div>
                  </StyledTableCell>
                  <StyledTableCell align="left">5 Million Points</StyledTableCell>
                  <StyledTableCell align="right">
                    <Chip
                      sx={{
                        backgroundColor: SystemTestStatusBgColor[renderQualityStatus],
                        color: SystemTestStatusColor[renderQualityStatus],
                      }}
                      label={renderQualityStatus}
                    />
                  </StyledTableCell>
                </StyledTableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ mt: 4 }}>
            <Typography variant="body2" fontWeight={500} sx={{ mb: 2 }}>
              Comments:
            </Typography>
            <Typography variant="body2" fontWeight={400}>
              {renderQualityStatus !== 'Good' && (
                <>
                  Oops! It appears that some of your system&apos;s hardware components fall below
                  the recommended threshold for optimal performance with our application. To ensure
                  a smooth and optimal user experience, it&apos;s important to have a Dedicated GPU
                  and 32 GB of RAM. These specifications are necessary for rendering the ideal 5
                  million points. Consider upgrading your hardware components to meet these
                  requirements.
                </>
              )}
              <Button style={{ color: '#165DFF' }} onClick={rerun}>
                Re-run test
              </Button>
            </Typography>
          </Box>
        </Box>
      )}
    </>
  );
};
